import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ChipPanel } from '../../designSystem/Chip';
import { PublicPageWrapper } from '../_pageWrappers/PublicPageWrapper';
import { GroupedQuestionAnswer, QuestionAnswerList } from './_components/QuestionAnswerList';
import { ThanksContactForm } from './_components/ThanksContactForm';
import { ThanksPageScore } from './_components/ThanksPageScore';
import {
    finalizeSurvyResponse,
    getSurvyBenchmark,
    getSurvy,
    getSurvyResponse,
    reopenSurvyResponse
} from '../survy/survyClientApi';
import { Survy, SurvyBenchmarkData, SurvyResponse } from '../../amorg-common';
import { applicationEndpoints } from '../../amorg-common/applicationEndpoints';
import { Card } from '../../designSystem/Card';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { ToggletipTheOne } from '../../designSystem/ToggletipTheOne';
import { H2 } from '../../designSystem/typography/Heading';
import { StatCard } from '../../designSystem/StatCard';
import { InfoChip } from '../../designSystem/Chip';

type PageState = {
    loading: boolean;
    submitting: boolean;
    reopening: boolean;
    survey: Survy | null;
    response: SurvyResponse | null;
    benchmark: SurvyBenchmarkData | null;
    error: string;
};

type SurveyOption = {
    value: string;
    text?: string;
    ratingPct?: number;
};

type RuntimeSurveyQuestion = {
    label?: string;
    question: {
        text: string;
        options?: SurveyOption[];
    };
};

export function SurvyThanksPage(): JSX.Element {
    const params = useParams<{ key: string; surveyId: string; responseId: string }>();
    const navigate = useNavigate();
    const [state, setState] = useState<PageState>({
        loading: true,
        submitting: false,
        reopening: false,
        survey: null,
        response: null,
        benchmark: null,
        error: ''
    });

    const [details, setDetails] = useState({
        name: '',
        email: '',
        organization: '',
        role: '',
        message: ''
    });

    useEffect(() => {
        const load = async () => {
            try {
                const [survey, response, benchmark] = await Promise.all([
                    getSurvy(params.key, params.surveyId),
                    getSurvyResponse(params.key, params.responseId),
                    getSurvyBenchmark(params.key, params.surveyId)
                ]);

                if (response && response.surveyId !== params.surveyId) {
                    setState({
                        loading: false,
                        submitting: false,
                        reopening: false,
                        survey,
                        response: null,
                        benchmark,
                        error: 'Besvarelses-id matcher ikke undersøgelsen.'
                    });
                    return;
                }

                setState({
                    loading: false,
                    submitting: false,
                    reopening: false,
                    survey,
                    response,
                    benchmark,
                    error: survey && response ? '' : 'Besvarelsen blev ikke fundet.'
                });

                if (response)
                    setDetails(response.respondentDetails);
            }
            catch {
                setState({ loading: false, submitting: false, reopening: false, survey: null, response: null, benchmark: null, error: 'Kunne ikke indlæse siden.' });
            }
        };

        load();
    }, [params.key, params.surveyId, params.responseId]);

    const answeredQuestions = useMemo(() => {
        if (!state.survey || !state.response)
            return [];

        const survey = state.survey;

        const benchmarkQuestionMap = new Map(
            (state.benchmark?.questions || []).map(question => [question.questionIndex, question])
        );

        return [...state.response.answers]
            .sort((a, b) => a.questionIndex - b.questionIndex)
            .map(answer => {
                const surveyQuestion = survey.questions[answer.questionIndex] as unknown as RuntimeSurveyQuestion | undefined;
                const matchingOption = surveyQuestion?.question.options?.find(option => option.value === answer.answerValue);
                const benchmarkQuestion = benchmarkQuestionMap.get(answer.questionIndex);

                return {
                    questionIndex: answer.questionIndex,
                    questionLabel: surveyQuestion?.label || 'Uden kategori',
                    questionText: surveyQuestion ? surveyQuestion.question.text : `Spørgsmål ${answer.questionIndex + 1}`,
                    answerText: matchingOption?.text || 'Ikke angivet',
                    ratingPct: typeof matchingOption?.ratingPct === 'number' ? matchingOption.ratingPct : undefined,
                    benchmarkRatingPct: benchmarkQuestion?.averageRatingPct
                };
            });
    }, [state.survey, state.response, state.benchmark]);

    const groupedAnsweredQuestions = useMemo<GroupedQuestionAnswer[]>(() => {
        const groups = new Map<string, typeof answeredQuestions>();

        for (const answeredQuestion of answeredQuestions) {
            const existing = groups.get(answeredQuestion.questionLabel);
            if (existing)
                existing.push(answeredQuestion);
            else
                groups.set(answeredQuestion.questionLabel, [answeredQuestion]);
        }

        return [...groups.entries()].map(([label, items]) => ({ label, items }));
    }, [answeredQuestions]);

    const overallScorePct = useMemo(() => {
        const ratings = answeredQuestions
            .map(item => item.ratingPct)
            .filter((value): value is number => typeof value === 'number');

        if (ratings.length === 0)
            return 0;

        const total = ratings.reduce((sum, value) => sum + value, 0);
        return Math.round(total / ratings.length);
    }, [answeredQuestions]);

    const overallBenchmarkScorePct = useMemo(() => {
        if (!state.benchmark)
            return 0;

        return Math.round(state.benchmark.averageRatingPct);
    }, [state.benchmark]);

    const isFinalized = !!state.response?.finalized;

    const lastQuestionIndex = useMemo(() => {
        if (!state.survey)
            return 0;

        return Math.max(0, state.survey.questions.length - 1);
    }, [state.survey]);

    async function handleFinalize() {
        if (!state.response)
            return;

        setState({ ...state, submitting: true, error: '' });

        try {
            await finalizeSurvyResponse(params.key, params.responseId, details);
            const updatedResponse = await getSurvyResponse(params.key, params.responseId);

            if (updatedResponse && updatedResponse.surveyId !== params.surveyId) {
                setState({ ...state, submitting: false, error: 'Besvarelses-id matcher ikke undersøgelsen.' });
                return;
            }

            setState({
                ...state,
                submitting: false,
                response: updatedResponse || state.response,
                error: ''
            });
        }
        catch {
            setState({ ...state, submitting: false, error: 'Kunne ikke afslutte besvarelsen.' });
        }
    }

    async function handleReopen() {
        setState({ ...state, reopening: true, error: '' });

        try {
            await reopenSurvyResponse(params.key, params.responseId);
            navigate(generatePath(applicationEndpoints.survy_key_surveyId_responseId, {
                key: params.key,
                surveyId: params.surveyId,
                responseId: params.responseId
            }));
        }
        catch {
            setState({ ...state, reopening: false, error: 'Kunne ikke genaabne besvarelsen.' });
        }
    }

    if (state.loading)
        return <PublicPageWrapper><Container>Indlæser...</Container></PublicPageWrapper>;

    if (!state.survey || !state.response)
        return <PublicPageWrapper><Container><ErrorText>{state.error || 'Besvarelsen blev ikke fundet.'}</ErrorText></Container></PublicPageWrapper>;

    return (
        <PublicPageWrapper>
            <SingleColumnLayout
                noDivider
                contentContainer={

                <Flex flexDirection='column' gap={32}>

                    <HeroSection>
                        <HeroTopRow>
                            <InfoChip variant="filled-dark">
                                {state.survey.title}
                            </InfoChip>
                        </HeroTopRow>

                        <Title>{isFinalized ? 'Tak for din besvarelse' : 'Gennemgå og afslut besvarelsen'}</Title>

                        <ChipPanel>
                            <StatCard header='Emner' value={`${groupedAnsweredQuestions.length}`} />
                            <StatCard header='Spørgsmål' value={`${state.survey.questions.length}`} />
                        </ChipPanel>
                    </HeroSection>

                    <Flex flexDirection='column' gap={8}>
                        <Flex alignItems='center' gap={8}>
                            <H2>Samlet score</H2>
                            <ToggletipTheOne>Benchmarken er baseret på alle indsendte svar.</ToggletipTheOne>
                        </Flex>
                        <Card variant='lightGray'>
                            <ThanksPageScore overallScorePct={overallScorePct} overallBenchmarkScorePct={overallBenchmarkScorePct} />                
                        </Card>
                    </Flex>

                    <Flex flexDirection='column' gap={8}> 
                        <H2>Svaroversigt</H2>
                        <Card variant='outlined'>
                            <QuestionAnswerList groups={groupedAnsweredQuestions} />
                        </Card>
                    </Flex>

                    {!isFinalized && (
                        <Flex flexDirection='column' gap={8}> 
                            <H2>Send til arbejdsmilørådgiver</H2>
                            <Card variant='outlined'>
                                <ThanksContactForm details={details} onDetailsChange={setDetails} />
                            </Card>
                        </Flex>
                    )}

                    {state.error && <ErrorText>{state.error}</ErrorText>}

                    <ButtonRow>
                        {!isFinalized && (
                            <GhostButton onClick={() => navigate(generatePath(applicationEndpoints.survy_key_surveyId_responseId_questions_questionIndex, {
                                key: params.key,
                                surveyId: params.surveyId,
                                responseId: params.responseId,
                                questionIndex: `${lastQuestionIndex}`
                            }))} disabled={state.submitting}>
                                Tilbage
                            </GhostButton>
                        )}

                        {!isFinalized && (
                            <PrimaryButton onClick={handleFinalize} disabled={state.submitting}>
                                {state.submitting ? 'Afslutter...' : 'Afslut og send'}
                            </PrimaryButton>
                        )}

                        {isFinalized && (
                            <PrimaryButton onClick={handleReopen} disabled={state.reopening}>
                                Genaabn og start forfra
                            </PrimaryButton>
                        )}
                    </ButtonRow>
                </Flex>
                } />
        </PublicPageWrapper>
    );
}

const Container = styled.main`
    width: 100%;
    max-width: 980px;
    margin: 0 auto;
    padding: 32px 20px;
`;

const HeroSection = styled.section`
    background: linear-gradient(135deg, #f3f7fb 0%, #f8fbff 100%);
    border: 1px solid #d9e3ef;
    border-radius: 12px;
    padding: 16px;
`;

const HeroTopRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    flex-wrap: wrap;
`;

const Title = styled.h1`
    margin: 0 0 8px;
    font-size: 2rem;
`;

const ButtonRow = styled.div`
    margin-top: 24px;
    padding-top: 14px;
    border-top: 1px solid #dde4eb;
    display: flex;
    justify-content: space-between;
    gap: 12px;
`;

const PrimaryButton = styled.button`
    border: 0;
    border-radius: 8px;
    background: #11365b;
    color: #fff;
    padding: 12px 18px;
    cursor: pointer;
    font-weight: 700;
`;

const GhostButton = styled.button`
    border: 1px solid #11365b;
    border-radius: 8px;
    background: #fff;
    color: #11365b;
    padding: 12px 18px;
    cursor: pointer;
`;

const ErrorText = styled.p`
    color: #b22727;
`;
